import type { Ref } from 'vue'

/** Viewport positioning shared by selection lists and non-modal menus. */
export function useUiFloating(
  open: Ref<boolean>,
  anchor: () => HTMLElement | undefined,
  panel: Ref<HTMLElement | undefined>,
  options: () => { width?: number | 'trigger'; minWidth?: number; placement?: 'start' | 'end' },
) {
  const style = ref<Record<string, string>>({})
  const ownerId = ref<string>()
  const sensitive = ref(false)
  function position() {
    const trigger = anchor()
    if (!open.value || !trigger) return
    const rect = trigger.getBoundingClientRect()
    sensitive.value = Boolean(trigger.closest('.analytics-sensitive'))
    const settings = options()
    const margin = 12
    const width = Math.min(
      window.innerWidth - margin * 2,
      Math.max(
        settings.minWidth ?? 0,
        settings.width === 'trigger'
          ? rect.width
          : (settings.width ?? panel.value?.offsetWidth ?? rect.width),
      ),
    )
    const below = window.innerHeight - rect.bottom - margin - 8
    const above = rect.top - margin - 8
    const upwards = below < Math.min(panel.value?.scrollHeight ?? 240, 240) && above > below
    const owner = trigger.closest<HTMLElement>('[data-ui-overlay-id]')
    ownerId.value = owner?.dataset.uiOverlayId
    const overlay = owner?.closest<HTMLElement>('.ui-overlay')
    style.value = {
      position: 'fixed',
      left: `${Math.max(margin, Math.min(settings.placement === 'end' ? rect.right - width : rect.left, window.innerWidth - width - margin))}px`,
      width: `${width}px`,
      top: upwards ? 'auto' : `${rect.bottom + 8}px`,
      bottom: upwards ? `${window.innerHeight - rect.top + 8}px` : 'auto',
      maxHeight: `${Math.max(80, upwards ? above : below)}px`,
      '--ui-options-height': `${Math.max(60, Math.min(340, upwards ? above : below) - 60)}px`,
      zIndex: overlay ? `calc(${getComputedStyle(overlay).zIndex} + 1)` : 'var(--z-popover)',
    }
  }
  watch(
    open,
    async () => {
      position()
      await nextTick()
      position()
    },
    { flush: 'sync' },
  )
  onMounted(() => {
    position()
    window.addEventListener('resize', position)
    document.addEventListener('scroll', position, true)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('resize', position)
    document.removeEventListener('scroll', position, true)
  })
  return { style, ownerId, sensitive, position }
}
